import React, { SFC } from "react";
import classNames from "classnames";
import { CSS_CLASSES } from "./image-css-classes";

function constructStyle(width: number, height: number, style: React.CSSProperties = {}): React.CSSProperties {
  const widthHeightStyles =
    width && !height ? { width } : !width && height ? { height } : width && height ? { width, height } : {};
  return { ...widthHeightStyles, ...style };
}

export type ObjectFitOption = "contain" | "cover" | "fill" | "none" | "scaleDown";

export type ObjectPositionOption =
  | "bottom"
  | "center"
  | "left"
  | "leftBottom"
  | "leftTop"
  | "right"
  | "rightBottom"
  | "rightTop"
  | "top";

export interface ImageProps {
  /**
   * The src URL for the image.
   * Can accept any value accepted by the `src` attribute of the HTML <img> element.
   */
  src: string;

  /**
   * Alternative text for the image. Required for good a11y.
   * Can accept any value accepted by the `alt` attribute of the HTML <img> element.
   */
  alt: string;

  /**
   * Specifies the `width` attribute of the <img> element.
   * It is recommended that this be used to avoid layout shifting.
   */
  width?: number;

  /**
   * Specifies the `height` attribute of the <img> element.
   * It is recommended that this be used to avoid layout shifting.
   */
  height?: number;

  /**
   * Applies the corresponding `object-fit` CSS style rule.
   */
  objectFit?: ObjectFitOption;

  /**
   * Applies the corresponding `object-position` CSS style rule.
   * This property only works correctly when the width and the height is set.
   */
  objectPosition?: ObjectPositionOption;

  /**
   * Apply/override styling by passing in custom CSS class-name(s) using this prop.
   */
  className?: string;

  /**
   * Apply/override styling by passing in custom CSS Rules object using this prop.
   */
  style?: React.CSSProperties;
}

/**
 * The Image component represents a low level primitive to render raster of
 * vector images using a URL. Any image content that needs to be rendered on
 * screen MUST use this component.
 */
export const Image: SFC<ImageProps> = ({
  src,
  alt,
  width,
  height,
  objectFit,
  objectPosition,
  className: classNameProp,
  style = {},
  ...restProps
}) => {
  const className = classNames(
    {
      [CSS_CLASSES.IMAGE_FIT_CONTAIN]: objectFit === "contain",
      [CSS_CLASSES.IMAGE_FIT_COVER]: objectFit === "cover",
      [CSS_CLASSES.IMAGE_FIT_FILL]: objectFit === "fill",
      [CSS_CLASSES.IMAGE_FIT_NONE]: objectFit === "none",
      [CSS_CLASSES.IMAGE_FIT_SCALE_DOWN]: objectFit === "scaleDown",

      [CSS_CLASSES.IMAGE_POSITION_CENTER]: objectPosition === "center",
      [CSS_CLASSES.IMAGE_POSITION_TOP]: objectPosition === "top",
      [CSS_CLASSES.IMAGE_POSITION_RIGHT_TOP]: objectPosition === "rightTop",
      [CSS_CLASSES.IMAGE_POSITION_RIGHT]: objectPosition === "right",
      [CSS_CLASSES.IMAGE_POSITION_RIGHT_BOTTOM]: objectPosition === "rightBottom",
      [CSS_CLASSES.IMAGE_POSITION_BOTTOM]: objectPosition === "bottom",
      [CSS_CLASSES.IMAGE_POSITION_LEFT_BOTTOM]: objectPosition === "leftBottom",
      [CSS_CLASSES.IMAGE_POSITION_LEFT]: objectPosition === "left",
      [CSS_CLASSES.IMAGE_POSITION_LEFT_TOP]: objectPosition === "leftTop",
    },
    classNameProp
  );

  return (
    <img
      style={constructStyle(width, height, style)}
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={className}
      {...restProps}
    />
  );
};
